import { FieldType } from '@databases/model/field';
import {
  type AiFieldGenerationFingerprint,
  type AiSummaryEmptyOutcome,
  type AiTextValue,
  type AiValueMetadata,
  type SingleTextValue,
  type ValueJSON
} from '@databases/model/value';
import * as Y from 'yjs';

import { isAiActor } from '../ai-metadata.ts';
import { YFieldType } from '../y-field.ts';

import { writeAdfToXmlFragment } from './adf-xml-conversion.ts';
import { TextValueStorageKeys, YTextValue } from './y-text-value.ts';

export const AiValueMetadataStorageKeys = {
  lastUpdatedAt: 'ai_lua',
  lastUpdatedBy: 'ai_lub',
  lastUpdatedUserAccountId: 'ai_lub_aid',
  generationFingerprintVersion: 'ai_gfv',
  sourceValueHash: 'ai_gsh',
  configurationHash: 'ai_gch',
  emptyOutcomeKind: 'ai_eok',
  emptyOutcomeGenerationFingerprintVersion: 'ai_eogfv',
  emptyOutcomeSourceValueHash: 'ai_eosh',
  emptyOutcomeConfigurationHash: 'ai_eoch'
} as const;

type AiTextLikeFieldType = FieldType.AiSummary;

const hasMetadata = (metadata: AiValueMetadata): boolean =>
  metadata.lastUpdatedAt !== undefined ||
  metadata.lastUpdatedBy !== undefined ||
  metadata.lastUpdatedUserAccountId !== undefined ||
  metadata.generation !== undefined ||
  metadata.emptyOutcome !== undefined;

const readString = (input: Y.Map<any>, key: string): string | undefined => {
  const value = input.get(key);
  return typeof value === 'string' ? value : undefined;
};

type GenerationFingerprintStorageKeys = {
  fingerprintVersion: string;
  sourceValueHash: string;
  configurationHash: string;
};

const generationFingerprintStorageKeys: GenerationFingerprintStorageKeys = {
  fingerprintVersion: AiValueMetadataStorageKeys.generationFingerprintVersion,
  sourceValueHash: AiValueMetadataStorageKeys.sourceValueHash,
  configurationHash: AiValueMetadataStorageKeys.configurationHash
};

const emptyOutcomeGenerationFingerprintStorageKeys: GenerationFingerprintStorageKeys = {
  fingerprintVersion: AiValueMetadataStorageKeys.emptyOutcomeGenerationFingerprintVersion,
  sourceValueHash: AiValueMetadataStorageKeys.emptyOutcomeSourceValueHash,
  configurationHash: AiValueMetadataStorageKeys.emptyOutcomeConfigurationHash
};

const readGenerationFingerprint = (
  input: Y.Map<any>,
  keys: GenerationFingerprintStorageKeys
): AiFieldGenerationFingerprint | undefined => {
  const fingerprintVersion = input.get(keys.fingerprintVersion);
  const sourceValueHash = readString(input, keys.sourceValueHash);
  if (sourceValueHash === undefined || sourceValueHash === '') {
    return undefined;
  }

  // V1 remains readable for mixed-client documents but is stale for refresh.
  // V2 adds the configuration hash so config-only changes invalidate summaries.
  if (fingerprintVersion === 1) {
    return { fingerprintVersion, sourceValueHash };
  }

  const configurationHash = readString(input, keys.configurationHash);
  if (
    fingerprintVersion === 2 &&
    configurationHash !== undefined &&
    configurationHash !== ''
  ) {
    return { fingerprintVersion, sourceValueHash, configurationHash };
  }

  return undefined;
};

const readEmptyOutcome = (input: Y.Map<any>): AiSummaryEmptyOutcome | undefined => {
  const kind = input.get(AiValueMetadataStorageKeys.emptyOutcomeKind);
  if (kind !== 'no-content' && kind !== 'no-result') {
    return undefined;
  }

  const generation = readGenerationFingerprint(
    input,
    emptyOutcomeGenerationFingerprintStorageKeys
  );
  return generation === undefined ? undefined : { kind, generation };
};

const readAiValueMetadata = (input: Y.Map<any>): AiValueMetadata | undefined => {
  const lastUpdatedBy = input.get(AiValueMetadataStorageKeys.lastUpdatedBy);
  const metadata = {
    lastUpdatedAt: readString(input, AiValueMetadataStorageKeys.lastUpdatedAt),
    lastUpdatedBy: isAiActor(lastUpdatedBy) ? lastUpdatedBy : undefined,
    lastUpdatedUserAccountId: readString(
      input,
      AiValueMetadataStorageKeys.lastUpdatedUserAccountId
    ),
    generation: readGenerationFingerprint(input, generationFingerprintStorageKeys),
    emptyOutcome: readEmptyOutcome(input)
  };

  return hasMetadata(metadata) ? metadata : undefined;
};

const setOptionalString = (
  input: Y.Map<any>,
  key: string,
  value: string | undefined
): void => {
  if (value === undefined) {
    input.delete(key);
  } else {
    input.set(key, value);
  }
};

const setGenerationFingerprint = (
  input: Y.Map<any>,
  keys: GenerationFingerprintStorageKeys,
  generation: AiFieldGenerationFingerprint | undefined
): void => {
  if (generation === undefined) {
    input.delete(keys.fingerprintVersion);
    input.delete(keys.sourceValueHash);
    input.delete(keys.configurationHash);
    return;
  }

  input.set(keys.fingerprintVersion, generation.fingerprintVersion);
  input.set(keys.sourceValueHash, generation.sourceValueHash);
  if (generation.fingerprintVersion === 2) {
    input.set(keys.configurationHash, generation.configurationHash);
  } else {
    input.delete(keys.configurationHash);
  }
};

export class YAITextValue<
  T extends AiTextLikeFieldType = FieldType.AiSummary
> extends YTextValue<T> {
  constructor(
    private aiInput: Y.Map<any>,
    attach?: (map: Y.Map<any>) => void,
    fieldType: T = FieldType.AiSummary as T
  ) {
    super(aiInput, attach, fieldType);
  }

  get metadata(): AiValueMetadata | undefined {
    return readAiValueMetadata(this.aiInput);
  }

  override get value(): AiTextValue {
    const metadata = this.metadata;
    return {
      ...super.value,
      ...(metadata !== undefined && { metadata })
    };
  }

  setMetadata(metadata: AiValueMetadata): void {
    setOptionalString(
      this.aiInput,
      AiValueMetadataStorageKeys.lastUpdatedAt,
      metadata.lastUpdatedAt
    );

    if (metadata.lastUpdatedBy === undefined) {
      this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedBy);
    } else {
      this.aiInput.set(AiValueMetadataStorageKeys.lastUpdatedBy, metadata.lastUpdatedBy);
    }

    setOptionalString(
      this.aiInput,
      AiValueMetadataStorageKeys.lastUpdatedUserAccountId,
      metadata.lastUpdatedUserAccountId
    );

    setGenerationFingerprint(
      this.aiInput,
      generationFingerprintStorageKeys,
      metadata.generation
    );

    if (metadata.emptyOutcome === undefined) {
      this.aiInput.delete(AiValueMetadataStorageKeys.emptyOutcomeKind);
    } else {
      this.aiInput.set(
        AiValueMetadataStorageKeys.emptyOutcomeKind,
        metadata.emptyOutcome.kind
      );
    }
    setGenerationFingerprint(
      this.aiInput,
      emptyOutcomeGenerationFingerprintStorageKeys,
      metadata.emptyOutcome?.generation
    );
  }

  clearMetadata(): void {
    this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedAt);
    this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedBy);
    this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedUserAccountId);
    this.aiInput.delete(AiValueMetadataStorageKeys.generationFingerprintVersion);
    this.aiInput.delete(AiValueMetadataStorageKeys.sourceValueHash);
    this.aiInput.delete(AiValueMetadataStorageKeys.configurationHash);
    this.aiInput.delete(AiValueMetadataStorageKeys.emptyOutcomeKind);
    this.aiInput.delete(
      AiValueMetadataStorageKeys.emptyOutcomeGenerationFingerprintVersion
    );
    this.aiInput.delete(AiValueMetadataStorageKeys.emptyOutcomeSourceValueHash);
    this.aiInput.delete(AiValueMetadataStorageKeys.emptyOutcomeConfigurationHash);
  }

  setAIGeneratedContent(value: SingleTextValue, date = new Date()): void {
    this.setTextContent(value);
    this.setMetadata({
      lastUpdatedAt: date.toISOString(),
      lastUpdatedBy: 'ai'
    });
  }

  markUserUpdated(accountId: string | undefined, date = new Date()): void {
    this.setMetadata({
      lastUpdatedAt: date.toISOString(),
      lastUpdatedBy: 'user',
      ...(accountId !== undefined && { lastUpdatedUserAccountId: accountId })
    });
  }

  setEmptyOutcome(outcome: AiSummaryEmptyOutcome): void {
    this.delete(0, this.plainTextValue.length);
    this.setMetadata({ emptyOutcome: outcome });
  }

  override toJSON(): ValueJSON<T, AiTextValue> {
    return {
      type: this.type,
      value: this.value
    };
  }

  static fromAiTextJSON<T extends AiTextLikeFieldType>(json: ValueJSON<T, AiTextValue>) {
    const metadata = json.value.metadata;
    const entries: Array<[string, unknown]> = [
      [TextValueStorageKeys.type, YFieldType.AI_SUMMARY],
      [TextValueStorageKeys.plainText, new Y.Text(json.value.plain ?? '')]
    ];

    if (json.value.rich !== undefined) {
      const fragment = new Y.XmlFragment();
      writeAdfToXmlFragment(json.value.rich, fragment);
      entries.push([TextValueStorageKeys.richText, fragment]);
    }

    if (metadata?.lastUpdatedAt !== undefined) {
      entries.push([AiValueMetadataStorageKeys.lastUpdatedAt, metadata.lastUpdatedAt]);
    }

    if (metadata?.lastUpdatedBy !== undefined) {
      entries.push([AiValueMetadataStorageKeys.lastUpdatedBy, metadata.lastUpdatedBy]);
    }

    if (metadata?.lastUpdatedUserAccountId !== undefined) {
      entries.push([
        AiValueMetadataStorageKeys.lastUpdatedUserAccountId,
        metadata.lastUpdatedUserAccountId
      ]);
    }

    if (metadata?.generation !== undefined) {
      entries.push([
        AiValueMetadataStorageKeys.generationFingerprintVersion,
        metadata.generation.fingerprintVersion
      ]);
      entries.push([
        AiValueMetadataStorageKeys.sourceValueHash,
        metadata.generation.sourceValueHash
      ]);
      if (metadata.generation.fingerprintVersion === 2) {
        entries.push([
          AiValueMetadataStorageKeys.configurationHash,
          metadata.generation.configurationHash
        ]);
      }
    }

    if (metadata?.emptyOutcome !== undefined) {
      entries.push([
        AiValueMetadataStorageKeys.emptyOutcomeKind,
        metadata.emptyOutcome.kind
      ]);
      entries.push([
        AiValueMetadataStorageKeys.emptyOutcomeGenerationFingerprintVersion,
        metadata.emptyOutcome.generation.fingerprintVersion
      ]);
      entries.push([
        AiValueMetadataStorageKeys.emptyOutcomeSourceValueHash,
        metadata.emptyOutcome.generation.sourceValueHash
      ]);
      if (metadata.emptyOutcome.generation.fingerprintVersion === 2) {
        entries.push([
          AiValueMetadataStorageKeys.emptyOutcomeConfigurationHash,
          metadata.emptyOutcome.generation.configurationHash
        ]);
      }
    }

    const map = new Y.Map(entries);

    return new YAITextValue(map, undefined, json.type);
  }

  private setTextContent(value: SingleTextValue): void {
    if (value.rich !== undefined) {
      this.setContent(value.rich);
      return;
    }

    const existingLength = this.plainTextValue.length;
    if (existingLength > 0) {
      this.delete(0, existingLength);
    }

    if (value.plain) {
      this.insert(0, value.plain);
    }
  }
}
